// ==============================================
// Tournaments Listing Page
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tournamentsAPI, citiesAPI } from '../services/api';
import { Tournament, City } from '../types';

const Tournaments: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load cities
      const citiesResponse = await citiesAPI.getAll();
      if (citiesResponse.success && citiesResponse.data) {
        setCities(citiesResponse.data.cities || []);
      }

      // Load tournaments
      const params: any = {};
      if (selectedCity) params.city_id = parseInt(selectedCity);
      if (selectedStatus) params.status = selectedStatus;

      const tournamentsResponse = await tournamentsAPI.getAll(params);
      
      if (tournamentsResponse.success && tournamentsResponse.data) {
        setTournaments(tournamentsResponse.data.tournaments || []);
      } else {
        setError(tournamentsResponse.message || 'Failed to load tournaments');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedCity, selectedStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTournaments = tournaments.filter(tournament =>
    tournament.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'open': return 'status-open';
      case 'ongoing': return 'status-ongoing';
      case 'completed': return 'status-completed';
      case 'full': return 'status-full';
      case 'draft': return 'status-draft';
      default: return 'status-completed';
    }
  };

  const isMockLive = (tournament: Tournament) => tournament.status === 'open' && tournament.id % 2 === 0;

  const mockProgress = (tournament: Tournament) => {
    const participantCount = Number(tournament.participant_count || 0);
    const max = Number(tournament.max_participants || 0);
    if (max > 0) return Math.min(96, Math.round((participantCount / max) * 100));
    return 35 + (tournament.id % 55);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="page">
      <div className="container page-inner">
        <div className="page-header">
          <h2>Turnee</h2>
          <p>Gaseste si inscrie-te la turnee de tenis de masa</p>
        </div>

        <div className="filter-bar">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Cauta turnee…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
                <option value="">Toate orasele</option>
                {cities.map(city => <option key={city.id} value={city.id}>{city.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="">Toate statusurile</option>
                <option value="open">Deschis</option>
                <option value="ongoing">In desfasurare</option>
                <option value="completed">Finalizat</option>
                <option value="full">Complet</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" onClick={() => { setSearchTerm(''); setSelectedCity(''); setSelectedStatus(''); }}>
                Sterge
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="tt-loading">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Se incarca…</span></div>
            <span>Se incarca turneele…</span>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          <>
            <p className="result-count">{filteredTournaments.length} {filteredTournaments.length !== 1 ? 'turnee gasite' : 'turneu gasit'}</p>

            {filteredTournaments.length === 0 ? (
              <div className="tt-empty">
                <div className="tt-empty-icon">🏆</div>
                <h5>Niciun turneu gasit</h5>
                <p>Incearca sa ajustezi criteriile de cautare</p>
              </div>
            ) : (
              <div className="row g-4">
                {filteredTournaments.map(tournament => (
                  <div key={tournament.id} className="col-md-6 col-lg-4">
                    <div className="entity-card">
                      <div className="entity-card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="fw-semibold mb-0" style={{ maxWidth: '70%' }}>{tournament.title}</h6>
                          <div className="d-flex flex-column align-items-end gap-1">
                            <span className={`badge ${getStatusBadgeClass(tournament.status)}`}>{tournament.status}</span>
                            {isMockLive(tournament) && <span className="badge bg-danger">live</span>}
                          </div>
                        </div>

                        {tournament.club && (
                          <p style={{ fontSize: '.82rem', color: 'var(--tt-text-muted)' }} className="mb-1">
                            🏓 {tournament.club.name}
                          </p>
                        )}

                        <p style={{ fontSize: '.82rem', color: 'var(--tt-text-muted)' }} className="mb-2">
                          📅 {formatDate(tournament.start_date)}
                          {tournament.end_date && tournament.end_date !== tournament.start_date && (
                            <> – {formatDate(tournament.end_date)}</>
                          )}
                        </p>

                        {(tournament.tournament_type_name || tournament.category) && (
                          <span className="badge cat-a mb-2">{tournament.tournament_type_name || tournament.category}</span>
                        )}

                        {tournament.description && (
                          <p style={{ fontSize: '.85rem', color: 'var(--tt-text-2)' }} className="mb-2">
                            {tournament.description.length > 100 ? `${tournament.description.substring(0, 100)}…` : tournament.description}
                          </p>
                        )}

                        <div className="d-flex justify-content-between" style={{ fontSize: '.8rem', color: 'var(--tt-text-muted)' }}>
                          <span>👥 {tournament.participant_count || 0}/{tournament.max_participants || '∞'} jucatori</span>
                          {tournament.registration_deadline && (
                            <span>Termen: {new Date(tournament.registration_deadline).toLocaleDateString('ro-RO')}</span>
                          )}
                        </div>

                        {isMockLive(tournament) && (
                          <div className="mt-2">
                            <div className="d-flex justify-content-between" style={{ fontSize: '.75rem', color: 'var(--tt-text-muted)' }}>
                              <span>Progres tragere</span>
                              <span>{mockProgress(tournament)}%</span>
                            </div>
                            <div style={{ background: '#e2e8f0', borderRadius: 999, height: 6, marginTop: 4 }}>
                              <div
                                style={{
                                  width: `${mockProgress(tournament)}%`,
                                  background: 'linear-gradient(90deg, #21257B, #EF8628)',
                                  borderRadius: 999,
                                  height: '100%',
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="entity-card-footer">
                        <Link to={`/tournament/${tournament.id}`} className="btn btn-primary btn-sm w-100">Vezi detalii</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Tournaments;
